// @flow

import { ChangeEvent, useContext, useEffect, useState } from "react";

import Tooltip from "@kea-mod/common/tooltip";
import { useTranslation } from "react-i18next";
import { SchemaTree, SchemaTreeType } from "../../../types/SchemaTree";
import { Schema } from "../../../types/Schema";
import { ApiContext } from "../../../context/KeaAPI/newApi";

type Props = { parentSchemaId: number; closeAction: () => void; visible: boolean };

export const TreeViewAddChildSchemaComponent = (props: Props) => {
  const { t } = useTranslation();

  const { schemaApi, schemaTreeApi } = useContext(ApiContext);

  const parentSchema = schemaApi.elements.find((schema) => schema.id === props.parentSchemaId);
  if (!parentSchema)
    throw Error("Unknown schema with id " + props.parentSchemaId + " in TreeViewAddChildSchemaComponent.");

  const [schemaCreated, setSchemaCreated] = useState(false);
  // can be used to reset.
  const defaultState = {
    name: "",
    description: "",
    weight: "",
    public: false,
  };
  const [newSchema, setNewSchema] = useState(defaultState);

  const reset = () => {
    setNewSchema(defaultState);
    setSchemaCreated(false);
  };

  // reset the form every time this component is shown
  useEffect(() => {
    if (props.visible) reset();
  }, [props.visible]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewSchema({ ...newSchema, [event.target.name]: event.target.value });
  };

  const handleCheckboxChange = (event: any) => {
    setNewSchema({ ...newSchema, [event.target.name]: event.target.checked });
  };

  const createSchema = async () => {
    const weightInt = parseInt(newSchema.weight, 10);
    if (schemaCreated) {
      alert(t("alreadyCreatingSchema"));
      return;
    } else if ([newSchema.name, newSchema.description, newSchema.weight].some((field) => field.trim() === "")) {
      alert(t("fillAllFields"));
      return;
    } else if (!(0 <= weightInt && weightInt <= 100)) {
      // negative or
      alert(t("weightBetween0And100"));
      return;
    }

    // create child schema
    let rawNewSchema: Schema = {
      name: newSchema.name,
      description: newSchema.description,
      publicSchema: newSchema.public,
      createdAt: "",
      updatedAt: "",
      id: -1,
    };
    let schemaId = await schemaApi.create(rawNewSchema);

    if (!schemaId) {
      alert(t("errorCreatingSchema"));
      return;
    }

    // create edge
    let newEdge: SchemaTree = {
      type: SchemaTreeType.SCHEMA,
      childId: schemaId,
      parentId: props.parentSchemaId,
      weight: parseInt(newSchema.weight, 10),
      scenario: false,
      language: false,
      interactionData: false,
      createdAt: "",
      updatedAt: "",
      id: -1,
    };

    let edgeID = await schemaTreeApi.create(newEdge);
    console.log("Child schema created with ID: " + schemaId + "; edge (schema tree) created with ID: " + edgeID);

    setSchemaCreated(true);
    // Not sure if it is possible to hit the button between this and the next line. But since both setXXX-functions
    // are async, it doesn't hurt track the state of the edge creation.
    reset();
    props.closeAction();
  };

  return (
    <div className="container mt-6" style={{ display: props.visible ? undefined : "none" }}>
      <div className="section">
        <div className="columns is-centered">
          <div className="column is-three-quarters">
            <div className="box">
              <h4 className="title is-4">{t("addChildSchema")}</h4>
              <div className="field">
                <label className="label">{t("name")}</label>
                <input
                  className="input"
                  type="text"
                  name="name"
                  value={newSchema.name ?? ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="field">
                <label className="label">{t("description")}</label>
                <input
                  className="input"
                  type="text"
                  name="description"
                  value={newSchema.description ?? ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="field">
                <label className="label">
                  {t("weight")}
                  <Tooltip
                    icon="circle-info"
                    class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                    content={t("weightTooltip")}
                  ></Tooltip>
                </label>
                <input
                  className="input"
                  type="text"
                  name="weight"
                  value={newSchema.weight ?? ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="field">
                <label className="label">{t("schemaispublic")}</label>
                <input name="public" type="checkbox" checked={newSchema.public} onChange={handleCheckboxChange} />
              </div>
              <div className="columns mt-4">
                <button
                  className="button is-danger"
                  onClick={() => {
                    reset();
                    props.closeAction();
                  }}
                >
                  {t("cancel")}
                </button>
                <button className="button is-normal is-success" onClick={createSchema}>
                  {t("create")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
